import { FunctionComponent, Suspense } from 'react';
import { Navigate, Route, createBrowserRouter, createRoutesFromElements } from 'react-router';
import { RouterProvider } from 'react-router/dom';

import { Loader } from './components/feedback/loader';
import { RouteErrorBoundary } from './components/feedback/route-error-boundary';
import { Fullscreen } from './components/layout/fullscreen';
import { CompanyAccessGuard } from './containers/company-access-guard';
import { lazyWithReload } from './utils/dynamic-import-reload';
import { useAuth } from './modules/auth/hooks/use-auth';
import { hasAdministrationOnlyAccess } from './modules/auth/utils/invoice-permissions';

const DashboardLayout = lazyWithReload(() => import('./app/dashboard/layout'));
const DashboardIndexPage = lazyWithReload(() => import('./app/dashboard/index.page'));
const DashboardHome = () => {
  const { permissions } = useAuth();
  return hasAdministrationOnlyAccess(permissions) ? <Navigate to="/profile" replace /> : <DashboardIndexPage />;
};
const CalendarIndexPage = lazyWithReload(() => import('./app/dashboard/calendar/index.page'));
const ActionsIndexPage = lazyWithReload(() => import('./app/dashboard/actions/index.page'));

const AuthLayout = lazyWithReload(() => import('./app/auth/layout'));
const AuthLoginPage = lazyWithReload(() => import('./app/auth/login.page'));
const AuthEmailLoginPage = lazyWithReload(() => import('./app/auth/email-login.page'));

// Trajectories
const TrajectoriesEditPage = lazyWithReload(() => import('./app/dashboard/trajectories/edit.page'));

const AdminIndexPage = lazyWithReload(() => import('./app/dashboard/admin/index.page'));

// Admin Trajectories
const AdminTrajectoriesIndexPage = lazyWithReload(() => import('./app/dashboard/admin/trajectories/index.page'));
const AdminTrajectoriesDetailPage = lazyWithReload(() => import('./app/dashboard/admin/trajectories/detail.page'));

// Cases
const ManagedCaseIndexPage = lazyWithReload(() => import('./app/dashboard/cases/managed-case.page'));

// Admin Cases
const AdminCasesIndexPage = lazyWithReload(() => import('./app/dashboard/admin/cases/index.page'));
const AdminCasesBoardPage = lazyWithReload(() => import('./app/dashboard/admin/cases/case-board.page'));

// Locations
const BranchesIndexPage = lazyWithReload(() => import('./app/dashboard/branches/index.page'));
const AdminBranchesIndexPage = lazyWithReload(() => import('./app/dashboard/admin/branches/index.page'));

// Roles
const RolesIndexPage = lazyWithReload(() => import('./app/dashboard/admin/roles/index.page.tsx'));

const AdminUsersIndexPage = lazyWithReload(() => import('./app/dashboard/admin/users/index.page'));
const AdminUsersMigrationPage = lazyWithReload(() => import('./app/dashboard/admin/users/migration.page.tsx'));
const AdminAgrosUsersDebugPage = lazyWithReload(() => import('./app/dashboard/admin/users/agros-debug.page.tsx'));
const AdminLegacyCatchupPage = lazyWithReload(() => import('./app/dashboard/admin/legacy-catchup/index.page.tsx'));
const AdminLegacyDebugPage = lazyWithReload(() => import('./app/dashboard/admin/legacy-debug/index.page.tsx'));
const AdminAuditIndexPage = lazyWithReload(() => import('./app/dashboard/admin/audit/index.page.tsx'));
const SupportEvidencePage = lazyWithReload(() => import('./app/dashboard/admin/lieke/evidence.page.tsx'));
const AdminOrganisationsIndexPage = lazyWithReload(() => import('./app/dashboard/admin/organisations/index.page.tsx'));
const AdminOrganisationsMergePage = lazyWithReload(() => import('./app/dashboard/admin/organisations/merge.page.tsx'));
const AdminCompaniesIndexPage = lazyWithReload(() => import('./app/dashboard/admin/companies/index.page.tsx'));
const AdminCompaniesDetailPage = lazyWithReload(() => import('./app/dashboard/admin/companies/detail.page.tsx'));
const AdminTrajectoriesMigrationPage = lazyWithReload(
  () => import('./app/dashboard/admin/trajectories/migration.page.tsx'),
);

// Data
const DataLayout = lazyWithReload(() => import('./app/dashboard/data/layout'));
const DataIndexPage = lazyWithReload(() => import('./app/dashboard/data/index.page'));
const DataTrajectoryProgressPage = lazyWithReload(() => import('./app/dashboard/data/trajectory-progress.page'));
const DataTimeSpentPage = lazyWithReload(() => import('./app/dashboard/data/time-spent.page'));
const ProfileIndexPage = lazyWithReload(() => import('./app/dashboard/profile/index.page'));
const DeveloperIndexPage = lazyWithReload(() => import('./app/dashboard/developer/index.page'));

// Public documents (magic link)
const PublicDocumentPage = lazyWithReload(() => import('./app/public/documents/public-document.page'));

const RouterFallback = () => (
  <Fullscreen className="bg-white">
    <Loader className="size-8" />
  </Fullscreen>
);

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route errorElement={<RouteErrorBoundary />}>
      {/* Public magic-link route (no auth) */}
      <Route path="/public/documents/:token" element={<PublicDocumentPage />} />
      {/* Auth routes */}
      <Route element={<AuthLayout />}>
        <Route path="/login" element={<AuthLoginPage />} />
      </Route>
      <Route element={<AuthLayout publicAccess />}>
        <Route path="/login/email" element={<AuthEmailLoginPage />} />
      </Route>

      {/* Dashboard routes */}
      <Route path="/" element={<DashboardLayout />}>
        <Route index element={<DashboardHome />} />
        <Route path="facturen" element={<Navigate to="/" replace />} />
        <Route path="dossierdatums" element={<Navigate to="/" replace />} />
        <Route path="actions" element={<ActionsIndexPage />} />
        <Route path="calendar" element={<CalendarIndexPage />} />
        <Route path="profile" element={<ProfileIndexPage />} />
        <Route path="developer" element={<DeveloperIndexPage />} />

        {/* Cases */}
        <Route path="cases">
          <Route path=":id/:casePerson" element={<ManagedCaseIndexPage />} />
        </Route>

        {/* Locations */}
        <Route path="locaties">
          <Route index element={<BranchesIndexPage />} />
        </Route>
        <Route path="branches" element={<Navigate to="/locaties" replace />} />

        {/* Data */}
        <Route path="data" element={<DataLayout />}>
          <Route index element={<DataIndexPage />} />
          <Route path="trajectvoortgang" element={<DataTrajectoryProgressPage />} />
          <Route path="tijdsbesteding" element={<DataTimeSpentPage />} />
        </Route>

        {/* Admin routes */}
        <Route path="/admin">
          <Route index element={<AdminIndexPage />} />
          <Route path="users" element={<AdminUsersIndexPage />} />
          <Route path="users/migration" element={<AdminUsersMigrationPage />} />
          <Route path="users/agros-debug" element={<AdminAgrosUsersDebugPage />} />
          <Route path="legacy-catchup" element={<AdminLegacyCatchupPage />} />
          <Route path="legacy-debug" element={<AdminLegacyDebugPage />} />
          <Route path="audit" element={<AdminAuditIndexPage />} />
          <Route path="lieke/evidence" element={<SupportEvidencePage />} />
          <Route path="organisations" element={<AdminOrganisationsIndexPage />} />
          <Route path="organisations/merge" element={<AdminOrganisationsMergePage />} />
          <Route path="branches" element={<AdminBranchesIndexPage />} />
          <Route element={<CompanyAccessGuard />}>
            <Route path="companies" element={<AdminCompaniesIndexPage />} />
            <Route path="companies/:id/detail" element={<AdminCompaniesDetailPage />} />
          </Route>
          <Route path="networks/*" element={<Navigate to="/" replace />} />
          <Route path="cases" element={<AdminCasesIndexPage />} />
          <Route path="cases/board" element={<AdminCasesBoardPage />} />
          <Route path="roles" element={<RolesIndexPage />} />
          <Route path="trajectories">
            <Route index element={<AdminTrajectoriesIndexPage />} />
            <Route path="migration" element={<AdminTrajectoriesMigrationPage />} />
            <Route path=":id/edit" element={<TrajectoriesEditPage />} />
            <Route path=":id/detail" element={<AdminTrajectoriesDetailPage />} />
          </Route>
        </Route>
      </Route>

      {/* Catch-all route for /dashboard redirect */}
      <Route path="/dashboard" element={<Navigate to="/" replace />} />
    </Route>,
  ),
);

export const Router: FunctionComponent = () => {
  return (
    <Suspense fallback={<RouterFallback />}>
      <RouterProvider router={router} unstable_useTransitions={false} />
    </Suspense>
  );
};
